import { useMemo } from "react";

import { useViewTransform } from "./useViewTransform";

export type FirstReceiverGap = {
  /** Total incomplete mapping rows attributable to this first receiver. */
  count: number;
  /** One line per gap type, e.g. "Alloy location(s) unmapped to a ship-to: 12". */
  messages: string[];
};

/** Rows attributed to no first receiver — surfaced by the global header alert. */
const UNATTRIBUTED = "[UNATTRIBUTED]";

/**
 * Per-first-receiver mapping-gap rollup for the current run. Reads the
 * `first_receiver_mapping_gaps` view transform (one row per
 * (first_receiver, source) with an incomplete-row count) and folds it into a
 * `Map<first_receiver, { count, messages }>` the Planning Console hangs a
 * `WarningIcon` off of, next to each first receiver.
 *
 * The "[UNATTRIBUTED]" bucket (gaps that don't resolve to an FR — e.g. WOS
 * targets for retailers absent from the customer master) is dropped here; those
 * stay in the global header alert.
 */
export function useFirstReceiverMappingGaps(
  runSettingsJson?: string,
): Map<string, FirstReceiverGap> {
  const vt = useViewTransform({
    transformId: "first_receiver_mapping_gaps",
    runSettings: runSettingsJson || undefined,
    initialParams: {},
  });

  return useMemo(() => {
    const map = new Map<string, FirstReceiverGap>();
    const table = vt.arrowTable;
    if (!table || table.numRows === 0) return map;

    const colFr = table.getChild("first_receiver");
    const colType = table.getChild("gap_type");
    const colCount = table.getChild("incomplete_count");

    for (let i = 0; i < table.numRows; i++) {
      const fr = String(colFr?.get(i) ?? "");
      if (!fr || fr === UNATTRIBUTED) continue;
      const count = Number(colCount?.get(i) ?? 0);
      if (!Number.isFinite(count) || count <= 0) continue;
      const gapType = String(colType?.get(i) ?? "").trim();

      const entry = map.get(fr) ?? { count: 0, messages: [] };
      entry.count += count;
      entry.messages.push(`${gapType}: ${count.toLocaleString()}`);
      map.set(fr, entry);
    }
    return map;
  }, [vt.arrowTable]);
}
